import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Divider,
  Grid,
  CircularProgress,
} from "@mui/material";
import {
  DeleteOutline as DeleteIcon,
  Add as AddIcon,
  KeyboardArrowRightOutlined,
} from "@mui/icons-material";
import axios from "axios";
import { useSelector } from "react-redux";
import { Trash2 } from "lucide-react";

const API_DOMAIN = "https://estbn.com/EstbnNew/public/api/";
const PROPERTIES_ENDPOINT = API_DOMAIN + "portal/properties";

// const AUTH_TOKEN = "1|uXWWHVAxUbLR94IuQ07nZUT5306vlnjfGFo4AUoR16bcf002";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 500,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: "16px",
  p: 0,
  display: "flex",
  flexDirection: "column",
  maxHeight: "90vh",
};

const headerFooterStyle = {
  p: "8px 24px 20px 24px",
};

const contentStyle = {
  flexGrow: 1,
  overflowY: "auto",
  p: "0px 5px 16px 24px",
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "#f1f1f1",
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#888",
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "#555",
  },
};

const InfoTextItem = ({ label, value }) => (
  <Typography variant="body1" sx={{ mb: 0.8 }}>
    {label}:{" "}
    <Typography component="span" fontWeight="bold">
      {value}
    </Typography>
  </Typography>
);

const UpdateProperties = ({
  open,
  onClose,
  initialPropertyData,
  onSaveSuccess,
  onDeleteSuccess,
  getCategoryById,
  handleOpenEditPropertyFormModal,
}) => {
  const defaultData = {
    id: null,
    name: "N/A",
    type: "String",
    isFilterable: false,
    isRequired: false,
    sortOrder: 0,
    values: [], // Default to empty array for values
    categoryId: null,
  };

  const { user } = useSelector((state) => state.auth);

  const AUTH_TOKEN = user?.token;

  const [propertyData, setPropertyData] = useState({ ...defaultData });
  const [propertyValues, setPropertyValues] = useState([]);
  const [valueErrors, setValueErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(false); // For Save API
  const [isDeleting, setIsDeleting] = useState(false); // For Delete API
  const [apiError, setApiError] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [originalPropertyValues, setOriginalPropertyValues] = useState([]); // To store initial state for diffing
  const [valuesToDelete, setValuesToDelete] = useState([]); // To track IDs of values marked for deletion

  useEffect(() => {
    if (open) {
      setApiError("");
      setIsLoading(false);
      setIsDeleting(false);
      setShowDeleteConfirm(false);
      if (initialPropertyData) {
        setPropertyData({
          id: initialPropertyData.id,
          name: initialPropertyData.name,
          type: initialPropertyData.type,
          isFilterable: initialPropertyData.is_filterable,
          isRequired: initialPropertyData.is_required,
          sortOrder: initialPropertyData.sort_order,
          categoryId: initialPropertyData.category_id,
        });

        const initialValuesMapped =
          Array.isArray(initialPropertyData.values) &&
          initialPropertyData.values.length > 0
            ? initialPropertyData.values.map((v, idx) => ({
                id: v.id !== undefined ? v.id : null, // Use existing ID or null if not present
                value: v.value !== undefined ? String(v.value) : String(v), // Handle if it's an object {value: 'abc'} or just a string 'abc'
                sort_order: v.sort_order !== undefined ? v.sort_order : idx + 1, // Use existing sort_order or default to index
              }))
            : [];

        setPropertyValues(
          initialValuesMapped.length > 0
            ? initialValuesMapped
            : [{ id: null, value: "", sort_order: 1 }]
        );
        setOriginalPropertyValues(initialValuesMapped); // Store original state for diffing
        setValueErrors(
          new Array(
            initialValuesMapped.length > 0 ? initialValuesMapped.length : 1
          ).fill("")
        );
        setValuesToDelete([]); // Reset deleted values
      } else {
        setPropertyData({ ...defaultData });
        setPropertyValues([{ id: null, value: "", sort_order: 1 }]); // Start with one empty new value
        setValueErrors([""]);
        setOriginalPropertyValues([]);
        setValuesToDelete([]);
      }
    }
  }, [initialPropertyData, open]);

  const validatePropertyValues = () => {
    let hasError = false;

    if (propertyData.type === "select" && propertyData.isRequired) {
      const cleanedValues = propertyValues
        .map((v) => v.value.trim())
        .filter((v) => v);
      const newErrors = propertyValues.map((item) => {
        if (!item.value.trim()) {
          hasError = true;
          return "This field is required.";
        }
        return "";
      });
      setValueErrors(newErrors);

      if (cleanedValues.length === 0 && propertyData.isRequired) {
        hasError = true;
        if (
          propertyValues.length === 0 ||
          (propertyValues.length === 1 && !propertyValues[0].value.trim())
        ) {
          setPropertyValues([{ id: null, value: "", sort_order: 1 }]); // Ensure at least one input field exists
          setValueErrors(["At least one value is required."]);
        } else {
          const allEmptyErrors = propertyValues.map((item) =>
            item.value.trim() === "" ? "At least one value is required." : ""
          );
          setValueErrors(allEmptyErrors);
        }
      }
    }
    return hasError;
  };

  const handleAddValue = () => {
    // Calculate new sort_order based on the current highest sort_order, or 1 if empty
    const newSortOrder =
      propertyValues.length > 0
        ? Math.max(...propertyValues.map((v) => v.sort_order)) + 1
        : 1;
    setPropertyValues([
      ...propertyValues,
      { id: null, value: "", sort_order: newSortOrder },
    ]);
    setValueErrors([...valueErrors, ""]);
  };

  const handleValueChange = (index, newValue) => {
    const newValues = [...propertyValues];
    newValues[index] = { ...newValues[index], value: newValue }; // Only update 'value' property
    setPropertyValues(newValues);
    if (valueErrors[index] && newValue.trim()) {
      const newErrors = [...valueErrors];
      newErrors[index] = "";
      setValueErrors(newErrors);
    }
  };

  const handleRemoveValue = (index) => {
    const valueToRemove = propertyValues[index];
    if (valueToRemove.id !== null && valueToRemove.id !== undefined) {
      setValuesToDelete((prev) => [...prev, valueToRemove.id]);
    }

    const newValues = propertyValues.filter((_, i) => i !== index);
    const newErrors = valueErrors.filter((_, i) => i !== index);

    if (
      propertyData.type === "select" &&
      propertyData.isRequired &&
      newValues.filter((v) => v.value.trim()).length === 0
    ) {
      setPropertyValues([{ id: null, value: "", sort_order: 1 }]); // Add a blank input
      setValueErrors(["At least one value is required."]);
    } else {
      const reorderedValues = newValues.map((v, i) => ({
        ...v,
        sort_order: v.sort_order || i + 1,
      }));
      setPropertyValues(
        reorderedValues.length > 0
          ? reorderedValues
          : [{ id: null, value: "", sort_order: 1 }]
      );
      setValueErrors(newErrors.length > 0 ? newErrors : [""]);
    }
  };

  const handleApiSave = async () => {
    setApiError("");

    if (
      ["select", "text"].includes(propertyData.type) &&
      propertyData.isRequired
    ) {
      if (validatePropertyValues()) {
        // This call will only validate for 'select' currently
        setApiError("Please fill in all required property values.");
        return;
      }
    }

    if (!initialPropertyData?.id || !propertyData?.categoryId) {
      setApiError("Property or Category ID is missing. Cannot update.");
      return;
    }

    setIsLoading(true);
    let success = true; // Assume success initially, will be set to false on critical error
    let updatedPropertyData = null;

    try {
      // 1. Update Property Metadata (PUT /portal/properties/{id})
      // This call will only send metadata, no values[] array.
      const propertyFormData = new FormData();
      propertyFormData.append("name", propertyData.name);
      propertyFormData.append("type", propertyData.type);
      propertyFormData.append(
        "is_filterable",
        propertyData.isFilterable ? "1" : "0"
      );
      propertyFormData.append(
        "is_required",
        propertyData.isRequired ? "1" : "0"
      );
      propertyFormData.append("sort_order", propertyData.sortOrder);
      propertyFormData.append("category_id", propertyData.categoryId);
      propertyFormData.append("_method", "PUT"); // Necessary for PUT requests with FormData

      const propertyUpdateResponse = await axios.post(
        `${PROPERTIES_ENDPOINT}/${initialPropertyData.id}`,
        propertyFormData,
        { headers: { Authorization: `Bearer ${AUTH_TOKEN}` } }
      );

      if (!propertyUpdateResponse.data?.success) {
        setApiError(
          propertyUpdateResponse.data.message ||
            "Failed to update property metadata."
        );
        setIsLoading(false);
        return; // Critical failure, stop here
      }
      updatedPropertyData = propertyUpdateResponse.data.data;

      // 2. Handle Property Value Operations (CRUD for values)
      if (["select", "text"].includes(propertyData.type)) {
        // Now also manage values for 'text' type
        const operations = [];
        // Create a map for quick lookup of original values by ID
        const originalValuesMap = new Map(
          originalPropertyValues.map((v) => [v.id, v])
        );

        // Identify values to Create or Update
        propertyValues.forEach((currentValue) => {
          const trimmedValue = currentValue.value.trim();

          // Skip empty values, validation should have caught required ones earlier
          if (!trimmedValue) {
            return;
          }

          if (currentValue.id === null || currentValue.id === undefined) {
            // CREATE NEW VALUE (POST /portal/properties/{id}/values)
            operations.push(async () => {
              const formData = new FormData();
              // Postman screenshot for POST Create a values uses values[0][value], values[0][sort_order]
              formData.append("values[0][value]", trimmedValue);
              formData.append("values[0][sort_order]", currentValue.sort_order); // Ensure sort_order is sent for new values
              try {
                const response = await axios.post(
                  `${PROPERTIES_ENDPOINT}/${initialPropertyData.id}/values`,
                  formData,
                  { headers: { Authorization: `Bearer ${AUTH_TOKEN}` } }
                );
                if (!response.data?.success)
                  throw new Error(
                    response.data.message ||
                      `Failed to create value: ${trimmedValue}`
                  );
                console.log(
                  `Created value '${trimmedValue}' successfully. Response:`,
                  response.data
                );
                return response.data.data; // Return data, possibly with new ID from backend
              } catch (err) {
                console.error(
                  `Error creating value '${trimmedValue}':`,
                  err.response?.data?.message || err.message
                );
                throw err; // Re-throw to be caught by Promise.allSettled
              }
            });
          } else {
            // UPDATE EXISTING VALUE (POST /portal/properties/{id}/values/{value_id} with _method: PUT)
            const originalValue = originalValuesMap.get(currentValue.id);
            // Only update if value or sort_order has changed AND it's not in valuesToDelete list
            if (
              !valuesToDelete.includes(currentValue.id) &&
              originalValue &&
              (originalValue.value !== trimmedValue ||
                originalValue.sort_order !== currentValue.sort_order)
            ) {
              operations.push(async () => {
                const formData = new FormData();
                formData.append("value", trimmedValue);
                formData.append("sort_order", currentValue.sort_order);
                formData.append("_method", "PUT"); // For the "POST Update a value for the p..." endpoint

                try {
                  const response = await axios.post(
                    // Use POST with _method PUT to update
                    `${PROPERTIES_ENDPOINT}/${initialPropertyData.id}/values/${currentValue.id}`,
                    formData,
                    { headers: { Authorization: `Bearer ${AUTH_TOKEN}` } }
                  );
                  if (!response.data?.success)
                    throw new Error(
                      response.data.message ||
                        `Failed to update value: ${trimmedValue}`
                    );
                  console.log(
                    `Updated value '${trimmedValue}' (ID: ${currentValue.id}) successfully.`
                  );
                  return response.data.data;
                } catch (err) {
                  console.error(
                    `Error updating value '${trimmedValue}' (ID: ${currentValue.id}):`,
                    err.response?.data?.message || err.message
                  );
                  throw err; // Re-throw to be caught by Promise.allSettled
                }
              });
            }
          }
        });

        // Handle Deleted Values (DEL /portal/properties/{id}/values/{value_id} OR DEL /portal/properties/{id}/values for bulk)
        if (valuesToDelete.length > 0) {
          operations.push(async () => {
            try {
              // Attempt bulk delete first using POST with X-HTTP-Method-Override: DELETE
              const bulkDeleteFormData = new FormData();
              valuesToDelete.forEach((id, idx) => {
                bulkDeleteFormData.append(`values[${idx}]`, id); // Assuming backend expects `values[]` of IDs for bulk delete
              });

              const deleteResponse = await axios.post(
                // Use POST to send body for DELETE
                `${PROPERTIES_ENDPOINT}/${initialPropertyData.id}/values`,
                bulkDeleteFormData,
                {
                  headers: {
                    Authorization: `Bearer ${AUTH_TOKEN}`,
                    "X-HTTP-Method-Override": "DELETE", // Signal to Laravel it's a DELETE
                  },
                }
              );
              if (!deleteResponse.data?.success)
                throw new Error(
                  deleteResponse.data.message || `Failed to delete bulk values.`
                );
              console.log(
                `Bulk deleted values ${valuesToDelete.join(", ")} successfully.`
              );
            } catch (err) {
              console.warn(
                `Bulk delete failed or API does not support DELETE with body (Error: ${
                  err.response?.data?.message || err.message
                }). Attempting individual deletes.`
              );
              // Fallback to individual deletes if bulk delete fails
              const individualDeletePromises = valuesToDelete.map(
                async (id) => {
                  try {
                    const individualDeleteResponse = await axios.delete(
                      `${PROPERTIES_ENDPOINT}/${initialPropertyData.id}/values/${id}`,
                      { headers: { Authorization: `Bearer ${AUTH_TOKEN}` } }
                    );
                    if (!individualDeleteResponse.data?.success)
                      throw new Error(
                        individualDeleteResponse.data.message ||
                          `Failed to delete value ID: ${id}`
                      );
                    console.log(`Individually deleted value ID: ${id}`);
                  } catch (indErr) {
                    console.error(
                      `Error deleting individual value ID: ${id}:`,
                      indErr.response?.data?.message || indErr.message
                    );
                    // Don't re-throw, let other operations complete
                  }
                }
              );
              await Promise.allSettled(individualDeletePromises); // Wait for all individual deletes
            }
          });
        }

        // Execute all identified value operations (create, update, delete)
        const results = await Promise.allSettled(operations.map((op) => op()));

        const failedValueOperations = results.filter(
          (result) => result.status === "rejected"
        );
        if (failedValueOperations.length > 0) {
          console.error("Some value operations failed:", failedValueOperations);
          setApiError(
            (prev) =>
              (prev ? prev + "\n" : "") +
              "Some property value changes could not be saved/deleted. Check console for details."
          );
          // Decided not to set overall success to false here, but log error and show message.
          // If property metadata saved, it's considered overall success.
        }
      }
    } catch (err) {
      const errorMsg =
        err.response?.data?.message ||
        "An error occurred during save operation.";
      setApiError(errorMsg);
      success = false; // Main property metadata update failed, critical
    } finally {
      setIsLoading(false);
      if (success) {
        onSaveSuccess?.(updatedPropertyData || initialPropertyData);
        onClose?.();
      }
      // Always refresh category after attempting save to ensure values are up-to-date
      getCategoryById();
    }
  };

  const handleDeleteIconClick = () => {
    setApiError("");
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!initialPropertyData?.id) {
      setApiError("Property ID is missing. Cannot delete.");
      return;
    }
    setIsDeleting(true);
    setApiError("");
    try {
      const response = await axios.delete(
        `${PROPERTIES_ENDPOINT}/${initialPropertyData.id}`,
        { headers: { Authorization: `Bearer ${AUTH_TOKEN}` } }
      );
      if (response.data?.success) {
        getCategoryById?.();
        onDeleteSuccess?.(initialPropertyData.id);
        setShowDeleteConfirm(false);
        onClose?.();
      } else {
        setApiError(response.data.message || "Failed to delete property.");
      }
    } catch (err) {
      setApiError(
        err.response?.data?.message ||
          "An error occurred while deleting the property."
      );
    } finally {
      setIsDeleting(false);
    }
  };

  if (!open) return null; // Conditionally render based on 'open' prop

  const handleEditModalClick = () => {
    onClose?.();
    if (handleOpenEditPropertyFormModal) {
      // Ensure initialPropertyData is passed with all necessary fields, including values with IDs
      const propertyFullData = {
        id: initialPropertyData.id,
        name: initialPropertyData.name,
        type: initialPropertyData.type,
        isFilterable: initialPropertyData.is_filterable,
        isRequired: initialPropertyData.is_required,
        sortOrder: initialPropertyData.sort_order,
        // Pass the values as they were received, ensuring IDs are propagated if they exist
        values: Array.isArray(initialPropertyData.values)
          ? initialPropertyData.values
          : initialPropertyData.values
          ? [initialPropertyData.values]
          : [],
        categoryId: initialPropertyData.category_id,
      };
      handleOpenEditPropertyFormModal(propertyFullData);
    }
  };

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box sx={modalStyle}>
          <Box
            sx={{
              ...headerFooterStyle,
              display: "flex",
              padding: "10px 20px",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid #eee",
            }}
          >
            <Typography variant="h6" fontWeight="bold" width={"75%"}>
              Property Profile
            </Typography>
            <IconButton
              onClick={handleEditModalClick}
              className=" d-flex justify-content-end"
              // style={{ width: "25%" }}
              color="default"
              aria-label="edit-property"
            >
              <KeyboardArrowRightOutlined />
            </IconButton>
          </Box>

          <Box sx={contentStyle}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 1,
                mb: 1,
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                Property Info
              </Typography>
            </Box>

            <Grid container spacing={0.3}>
              <Grid item xs={6}>
                <InfoTextItem label="Name" value={propertyData.name} />
              </Grid>
              <Grid item xs={6}>
                <InfoTextItem label="Type" value={propertyData.type} />
              </Grid>
              <Grid item xs={6}>
                <InfoTextItem
                  label="Is Filterable"
                  value={propertyData.isFilterable ? "True" : "False"}
                />
              </Grid>
              <Grid item xs={6}>
                <InfoTextItem
                  label="Is Required"
                  value={propertyData.isRequired ? "True" : "False"}
                />
              </Grid>
              <Grid item xs={6}>
                <InfoTextItem
                  label="Sort Order"
                  value={propertyData.sortOrder}
                />
              </Grid>
              <Grid item xs={6}>
                <InfoTextItem
                  label="Category ID"
                  value={propertyData?.categoryId || "N/A"}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 0.8, borderColor: "rgba(0, 0, 0, 0.12)" }} />

            <Typography variant="h6" fontWeight="bold" mt={0.5} mb={1.5}>
              Property Values
            </Typography>
            {/* MODIFIED: Now also shows value inputs for 'text' type */}
            {["select", "text"].includes(propertyData.type) ? (
              <>
                {propertyValues.map((item, index) => (
                  <Box
                    key={item.id || `new-${index}`}
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { sm: "center" },
                      gap: 1,
                      width: "100%",
                    }}
                  >
                    <TextField
                      fullWidth
                      className="mt-1"
                      size="small"
                      label={
                        index === 0 ? "Property Value" : `Value ${index + 1}`
                      }
                      placeholder="Add property value"
                      value={item.value}
                      onChange={(e) => handleValueChange(index, e.target.value)}
                      error={!!valueErrors[index]}
                      helperText={valueErrors[index]}
                      disabled={isLoading || isDeleting}
                    />
                    {(propertyValues.length > 1 ||
                      (propertyValues.length === 1 &&
                        item.value.trim() !== "")) && (
                      <IconButton
                        onClick={() => handleRemoveValue(index)}
                        color="error"
                        disabled={isLoading || isDeleting}
                        aria-label={`Remove value ${index + 1}`}
                        className="mb-2"
                      >
                        <Trash2 size={20} />
                        {/* <DeleteIcon /> */}
                      </IconButton>
                    )}
                  </Box>
                ))}

                {/* {propertyValues.map((item, index) => (
                        <Box key={item.id || `new-${index}`} sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label={index === 0 ? "Property Value" : `Value ${index + 1}`}
                                placeholder="Add property value"
                                value={item.value} // Access value from item object
                                onChange={(e) => handleValueChange(index, e.target.value)}
                                error={!!valueErrors[index]}
                                helperText={valueErrors[index]}
                                disabled={isLoading || isDeleting}
                            />
                            
                            {(propertyValues.length > 1 || (propertyValues.length === 1 && item.value.trim() !== '')) && (
                                <IconButton sx={{ ml: 1 }} onClick={() => handleRemoveValue(index)} color="error" disabled={isLoading || isDeleting} aria-label={`Remove value ${index + 1}`}>
                                    <DeleteIcon />
                                </IconButton>
                            )}
                        </Box>
                    ))} */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    width: "100%",
                  }}
                >
                  <Button
                    startIcon={<AddIcon fontSize="small" />}
                    onClick={handleAddValue}
                    disabled={isLoading || isDeleting}
                    size="small"
                    sx={{
                      textTransform: "none",
                      mt: 0.5,
                      color: "text.secondary",
                    }}
                  >
                    Add another value
                  </Button>
                </Box>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Values are not applicable for properties of type '
                {propertyData.type}'.
              </Typography>
            )}

            {apiError && !showDeleteConfirm && (
              <Typography color="error" mt={2} sx={{ wordBreak: "break-word" }}>
                {apiError}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              ...headerFooterStyle,
              display: "flex",
              justifyContent: "center",
              gap: 2,
            }}
          >
            <Button
              onClick={onClose}
              disabled={isLoading || isDeleting}
              variant="outlined"
              className=" w-25 h-100"
              sx={{
                color: "common.black",
                borderColor: "grey.400",
                "&:hover": {
                  backgroundColor: "grey.100",
                  borderColor: "grey.500",
                },
                borderRadius: "8px",
                // padding: "6px 16px",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              className=" w-25 h-100"
              onClick={handleApiSave}
              disabled={isLoading || isDeleting}
              sx={{
                backgroundColor: "common.black",
                color: "common.white",
                "&:hover": { backgroundColor: "grey.800" },
                borderRadius: "8px",
                // padding: "6px 16px",
                boxShadow: "none",
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Save"
              )}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Delete Confirmation Modal (remains unchanged) */}
      <Modal
        open={showDeleteConfirm}
        onClose={() => !isDeleting && setShowDeleteConfirm(false)}
      >
        <Box sx={{ ...modalStyle, width: 400, p: 0 }}>
          <Box
            sx={{
              ...headerFooterStyle,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              Confirm Deletion
            </Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            <Typography mb={3}>
              Are you sure you want to delete "{initialPropertyData?.name}"?
            </Typography>
            {apiError && showDeleteConfirm && (
              <Typography color="error" mb={2} sx={{ wordBreak: "break-word" }}>
                {apiError}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              ...headerFooterStyle,
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              borderTop: "1px solid #eee",
            }}
          >
            <Button
              onClick={() => setShowDeleteConfirm(false)}
              variant="outlined"
              sx={{
                color: "common.black",
                borderColor: "grey.400",
                "&:hover": {
                  backgroundColor: "grey.100",
                  borderColor: "grey.500",
                },
                borderRadius: "8px",
                padding: "6px 16px",
              }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              color="error"
              onClick={confirmDelete}
              disabled={isDeleting}
              sx={{
                borderRadius: "8px",
                padding: "6px 16px",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.error.dark,
                },
              }}
            >
              {isDeleting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Delete"
              )}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default UpdateProperties;
